// De dónde viene un fallo, y qué se le dice al cajero por eso.
//
// Cuando algo no sale contra Business Central hay dos historias muy distintas
// detrás, y al cajero le llegaban las dos con el mismo envoltorio:
//
// - **Business Central contestó y rechazó.** El motivo es suyo, está en su
//   idioma y es accionable: «ya existe ese documento», «falta el grupo
//   contable». Ese texto se enseña tal cual, porque es el que el cajero puede
//   corregir o repetirle a quien le ayude.
// - **No hubo respuesta.** La tienda se quedó sin red, o la petición expiró.
//   `axios` deja entonces un `error.message` crudo --«Network Error», «timeout
//   of 30000ms exceeded»--, que no le dice nada a quien está cobrando. Y salía
//   bajo el título «Error Business Central», que además es falso: Business
//   Central no dijo nada, ni se enteró.
//
// La diferencia entre las dos se ve en un solo sitio: si `axios` trae
// `response`, hubo respuesta. Es la misma comprobación que ya hacía la
// sincronización de clientes a mano; acá vive una vez.

/** Business Central contestó, y lo que dijo es para el cajero. */
export const ORIGEN_BC = "bc";

/** No hubo respuesta: red caída, petición expirada, navegador. */
export const ORIGEN_RED = "red";

/**
 * De dónde viene el fallo.
 *
 * @param {Error} error El error de `axios`.
 * @returns {"bc"|"red"}
 */
export const origenDelFallo = (error) => (error?.response ? ORIGEN_BC : ORIGEN_RED);

/** Lo que se le dice al cajero cuando no hubo respuesta. */
export const SIN_RESPUESTA = "Parece un problema de conexión: vuelve a intentarlo en un momento.";

/**
 * El aviso que ve el cajero, con el título que le corresponde.
 *
 * El título importa tanto como el texto: «Error Business Central» encima de un
 * fallo de red manda a buscar el problema donde no está.
 *
 * @param {object} datos
 * @param {"bc"|"red"} datos.origen
 * @param {string} [datos.mensaje]      Lo que contestó Business Central.
 * @param {string} [datos.queNoSePudo]  Qué no salió, en una frase: «No se pudo
 *                                      contabilizar la factura.»
 * @returns {{titulo: string, texto: string}}
 */
export const avisoDelFallo = ({ origen, mensaje, queNoSePudo = "" }) => {
  const texto = String(mensaje ?? "").trim();

  // El motivo sólo se oculta cuando **se sabe** que nadie contestó. Un origen
  // sin determinar con mensaje se trata como de Business Central: callarlo «por
  // si acaso» le quitaría al cajero lo único que podía corregir, y hay sitios
  // que todavía no etiquetan de dónde viene el fallo.
  if (origen !== ORIGEN_RED && texto) {
    return { titulo: "Error Business Central", texto };
  }

  const inicio = String(queNoSePudo ?? "").trim();
  return {
    titulo: "Sin conexión con Business Central",
    texto: inicio ? `${inicio} ${SIN_RESPUESTA}` : SIN_RESPUESTA,
  };
};

/**
 * Los textos con los que `axios` y el navegador nombran un fallo de red.
 *
 * No son mensajes: son el nombre interno del fallo. Al cajero no le dicen nada
 * y, peor, parecen un error de Business Central.
 */
const TEXTOS_DE_RED = [
  "network error",
  "failed to fetch",
  "timeout of",
  "exceeded",
  "err_network",
  "err_internet_disconnected",
  "econnrefused",
  "socket hang up",
];

/** Si un texto ya construido es en realidad el nombre de un fallo de red. */
export const esTextoDeFalloDeRed = (texto) => {
  const limpio = String(texto ?? "")
    .trim()
    .toLowerCase();
  if (!limpio) return false;
  return TEXTOS_DE_RED.some((t) => limpio.includes(t));
};

/**
 * Limpia un motivo que ya viene hecho cadena.
 *
 * Es la red de seguridad del borde: hay varios sitios que construyen el texto
 * antes de que nadie sepa de dónde vino el fallo --`error.message` metido en
 * una plantilla-- y buscarlos uno a uno ya falló una vez. Acá se reconoce el
 * texto por lo que es y se sustituye por algo que el cajero pueda usar.
 *
 * Lo que no es un fallo de red se devuelve intacto: el motivo de Business
 * Central es justamente lo que hay que enseñar.
 *
 * @param {string} texto
 * @returns {string}
 */
export const limpiarMotivoTecnico = (texto) =>
  esTextoDeFalloDeRed(texto) ? SIN_RESPUESTA : String(texto ?? "");

/**
 * El motivo para el cajero, a partir del error que se capturó.
 *
 * @param {Error} error
 * @param {string} [queNoSePudo]
 * @returns {string}
 */
export const motivoParaElCajero = (error, queNoSePudo = "") =>
  avisoDelFallo({
    origen: origenDelFallo(error),
    mensaje: error?.message,
    queNoSePudo,
  }).texto;
